import * as React from 'react';
import { useEffect, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { IconButton, Switch, Paper, Tooltip, TextField } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { sendServerRequest } from '../../../server/request';
import { openGlobalSnack } from '../../utils/snack';
import { CircularLoading } from '../../utils/loading';

const ENDPOINT = '/server/fgtsCommand/providerOrder';

type ProviderEntry = { name: string; enabled: boolean; bank_name?: string };

const LABELS: Record<string, string> = {
  girotech: 'Girotech',
  bms: 'BMS',
};

function label(name: string) {
  return LABELS[name] ?? name.toUpperCase();
}

/** Só nome + habilitação vão para o servidor (bank_name é informativo). */
function snapshot(list: ProviderEntry[]) {
  return JSON.stringify(list.map((p) => ({ name: p.name, enabled: p.enabled })));
}

export function FgtsProviderOrderPanel() {
  const [order, setOrder] = useState<ProviderEntry[]>([]);
  const [serverSnapshot, setServerSnapshot] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    load();
  }, []);

  function load() {
    setLoading(true);
    sendServerRequest('GET', `${ENDPOINT}/get`)
      .then((e) => {
        const list: ProviderEntry[] = e.data?.data?.order ?? [];
        setOrder(list);
        setServerSnapshot(snapshot(list));
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        openGlobalSnack('Não foi possível carregar a ordem dos bancos.', 'error');
      });
  }

  function move(index: number, dir: -1 | 1) {
    const target = index + dir;
    if (target < 0 || target >= order.length) return;
    const next = [...order];
    [next[index], next[target]] = [next[target], next[index]];
    setOrder(next);
  }

  function toggle(index: number, value: boolean) {
    const next = [...order];
    next[index] = { ...next[index], enabled: value };
    setOrder(next);
  }

  function save() {
    if (!order.some((p) => p.enabled)) {
      openGlobalSnack('Deixe pelo menos um banco habilitado.', 'error');
      return;
    }
    setSaving(true);
    sendServerRequest('POST', `${ENDPOINT}/set`, {
      order: order.map((p) => ({ name: p.name, enabled: p.enabled })),
    })
      .then((e) => {
        const list: ProviderEntry[] = e.data?.data?.order ?? order;
        setOrder(list);
        setServerSnapshot(snapshot(list));
        setSaving(false);
        openGlobalSnack('Ordem dos bancos atualizada.', 'success');
      })
      .catch(() => {
        setSaving(false);
        openGlobalSnack('Não foi possível salvar a ordem.', 'error');
      });
  }

  const dirty = snapshot(order) !== serverSnapshot;

  if (loading) return <CircularLoading />;

  return (
    <div style={{ maxWidth: 560, margin: '0 auto', textAlign: 'center' }}>
      <h3 style={{ marginBottom: 4 }}>Ordem de consulta dos bancos (FGTS)</h3>
      <p style={{ fontSize: 12, color: '#000000a0', marginTop: 0, marginBottom: 16 }}>
        A consulta de saldo tenta os bancos habilitados nesta ordem. Se o cliente não autorizou um banco,
        passa para o próximo. O pedido de autorização (bot, site e app) lista os bancos nesta mesma ordem.
      </p>

      {order.map((p, i) => (
        <Paper
          key={p.name}
          variant="outlined"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 12px',
            marginBottom: 8,
            opacity: p.enabled ? 1 : 0.55,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left' }}>
            <span style={{ fontWeight: 600, minWidth: 24 }}>{i + 1}º</span>
            <div>
              <div>{label(p.name)}</div>
              {p.bank_name && <div style={{ fontSize: 11, color: '#00000090' }}>{p.bank_name}</div>}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Tooltip title={p.enabled ? 'Habilitado' : 'Desabilitado'}>
              <Switch
                checked={p.enabled}
                onChange={(e) => toggle(i, e.target.checked)}
              />
            </Tooltip>
            <IconButton size="small" disabled={i === 0} onClick={() => move(i, -1)}>
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              disabled={i === order.length - 1}
              onClick={() => move(i, 1)}
            >
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
          </div>
        </Paper>
      ))}

      <div style={{ marginTop: 16 }}>
        <LoadingButton
          loading={saving}
          disabled={!dirty}
          variant="contained"
          onClick={save}
        >
          Salvar ordem
        </LoadingButton>
      </div>

      <AuthFlowToggle />
      <AuthBanksEditor />
    </div>
  );
}

/**
 * Quando pedir a autorização dos bancos no app do FGTS. Desligado (padrão): o
 * sistema consulta primeiro e só pede se o banco responder que não está
 * autorizado. Ligado: pede antes da 1ª consulta. Vale para bot, site e app;
 * salva na hora.
 */
function AuthFlowToggle() {
  const [askFirst, setAskFirst] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    sendServerRequest('GET', `${ENDPOINT}/authFlow`)
      .then((e) => setAskFirst(e.data?.data?.ask_auth_before_consult === true))
      .catch(() => openGlobalSnack('Não foi possível carregar o fluxo de autorização.', 'error'));
  }, []);

  function change(value: boolean) {
    setSaving(true);
    sendServerRequest('POST', `${ENDPOINT}/authFlow`, { ask_auth_before_consult: value })
      .then((e) => {
        setAskFirst(e.data?.data?.ask_auth_before_consult === true);
        setSaving(false);
        openGlobalSnack(value ? 'Agora pede a autorização antes de consultar.' : 'Agora consulta antes de pedir a autorização.', 'success');
      })
      .catch(() => {
        setSaving(false);
        openGlobalSnack('Não foi possível salvar o fluxo de autorização.', 'error');
      });
  }

  if (askFirst === null) return null;

  return (
    <Paper variant="outlined" style={{ marginTop: 24, padding: '12px 16px', textAlign: 'left' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div style={{ fontWeight: 600 }}>Pedir autorização dos bancos antes de consultar</div>
          <div style={{ fontSize: 12, color: '#000000a0' }}>
            {askFirst
              ? 'Ligado: bot, site e app pedem para autorizar os bancos no app do FGTS antes da primeira consulta.'
              : 'Desligado: o sistema consulta primeiro e só pede a autorização se o banco responder que não está autorizado.'}
          </div>
        </div>
        <Switch checked={askFirst} disabled={saving} onChange={(e) => change(e.target.checked)} />
      </div>
    </Paper>
  );
}

/** Extrai a mensagem de erro do JSONResponse ({data, hasError, error}). */
function parseError(err: any, fallback: string): string {
  try {
    const txt = err?.target?.responseText;
    if (txt) {
      const j = JSON.parse(txt);
      if (j?.error && typeof j.error === 'string') return j.error;
    }
  } catch (e) {}
  return fallback;
}

type AuthBanksForm = {
  banks: { provider: string; enabled: boolean; name: string }[];
  video_url: string;
  image_url: string;
};

function formSnapshot(f: AuthBanksForm) {
  return JSON.stringify({
    banks: f.banks.map((b) => ({ provider: b.provider, name: b.name.trim() })),
    video_url: f.video_url.trim(),
    image_url: f.image_url.trim(),
  });
}

/**
 * Nome de cada banco (como aparece no app do FGTS) e a mídia de autorização:
 * um vídeo tutorial e uma imagem que mostram todos os bancos. Bot, site e app
 * leem daqui; mudar a mídia não exige deploy.
 */
function AuthBanksEditor() {
  const [form, setForm] = useState<AuthBanksForm | null>(null);
  const [serverSnapshot, setServerSnapshot] = useState('');
  const [saving, setSaving] = useState(false);

  function apply(data: any) {
    const f: AuthBanksForm = {
      banks: (data?.banks ?? []).map((b: any) => ({ provider: b.provider, enabled: !!b.enabled, name: b.name ?? '' })),
      video_url: data?.video_url ?? '',
      image_url: data?.image_url ?? '',
    };
    setForm(f);
    setServerSnapshot(formSnapshot(f));
  }

  useEffect(() => {
    sendServerRequest('GET', `${ENDPOINT}/authBanks`)
      .then((e) => apply(e.data?.data))
      .catch(() => openGlobalSnack('Não foi possível carregar os bancos de autorização.', 'error'));
  }, []);

  function save() {
    if (!form) return;
    setSaving(true);
    sendServerRequest('POST', `${ENDPOINT}/authBanks`, {
      banks: form.banks.map((b) => ({ provider: b.provider, name: b.name.trim() })),
      video_url: form.video_url.trim(),
      image_url: form.image_url.trim(),
    })
      .then((e) => {
        apply(e.data?.data);
        setSaving(false);
        openGlobalSnack('Autorização dos bancos atualizada.', 'success');
      })
      .catch((e) => {
        setSaving(false);
        openGlobalSnack(parseError(e, 'Não foi possível salvar. Confira os nomes e se as URLs são https.'), 'error');
      });
  }

  if (!form) return null;
  const dirty = formSnapshot(form) !== serverSnapshot;

  return (
    <Paper variant="outlined" style={{ marginTop: 24, padding: '12px 16px', textAlign: 'left' }}>
      <div style={{ fontWeight: 600 }}>Mensagem de autorização dos bancos</div>
      <div style={{ fontSize: 12, color: '#000000a0', marginBottom: 12 }}>
        Nome de cada banco como aparece no app do FGTS e a mídia (vídeo e imagem) com a autorização de todos eles.
        Bot, site e app usam estes dados. URLs precisam ser https; deixe vazio para não enviar a mídia.
      </div>

      {form.banks.map((b, i) => (
        <TextField
          key={b.provider}
          fullWidth
          size="small"
          margin="dense"
          label={`Nome no app do FGTS — ${label(b.provider)}${b.enabled ? '' : ' (desabilitado)'}`}
          value={b.name}
          inputProps={{ maxLength: 80 }}
          onChange={(e) => {
            const banks = [...form.banks];
            banks[i] = { ...banks[i], name: e.target.value };
            setForm({ ...form, banks });
          }}
        />
      ))}

      <TextField
        fullWidth
        size="small"
        margin="dense"
        label="URL do vídeo tutorial"
        placeholder="https://..."
        value={form.video_url}
        onChange={(e) => setForm({ ...form, video_url: e.target.value })}
        helperText={form.video_url.trim() ? <a href={form.video_url.trim()} target="_blank" rel="noopener noreferrer">Abrir vídeo</a> : 'Sem vídeo'}
      />
      <TextField
        fullWidth
        size="small"
        margin="dense"
        label="URL da imagem"
        placeholder="https://..."
        value={form.image_url}
        onChange={(e) => setForm({ ...form, image_url: e.target.value })}
        helperText={form.image_url.trim() ? <a href={form.image_url.trim()} target="_blank" rel="noopener noreferrer">Abrir imagem</a> : 'Sem imagem'}
      />
      {form.image_url.trim() && (
        <img src={form.image_url.trim()} alt="Prévia da imagem de autorização" style={{ maxWidth: 160, maxHeight: 120, borderRadius: 6, marginTop: 4 }} />
      )}

      <div style={{ marginTop: 12, textAlign: 'center' }}>
        <LoadingButton loading={saving} disabled={!dirty} variant="contained" onClick={save}>
          Salvar autorização
        </LoadingButton>
      </div>
    </Paper>
  );
}
